import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Calendar, RefreshCw } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getGreeting } from '../utils/formatters';
import { DATE_FILTERS } from '../utils/constants';

export function CommandBar({
  user, logoUrl, brandName, dateRange, startDate, endDate,
  isCustomDateOpen, unreadCount, updatedAt,
  onDateRangeChange, onCustomDateChange, onToggleCustomDate, onNotificationToggle, onRefresh, isRefreshing,
}) {
  const greeting = getGreeting();
  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: Logo + Greeting */}
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl && (
            <img src={logoUrl} alt={brandName || ''} className="h-8 object-contain flex-shrink-0" style={{ maxWidth: 120 }} />
          )}
          <div className="min-w-0">
            <span className="text-sm font-medium text-gray-700 truncate block">
              {greeting}, <span className="font-semibold text-gray-900">{firstName}</span>
            </span>
            <span className="text-[10px] text-gray-400">Dashboard Overview</span>
          </div>
        </div>

        {/* Center: Date pills */}
        <div className="hidden md:flex items-center">
          <div className="pill-tab-container">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => onDateRangeChange(f.value)}
                className={`pill-tab ${dateRange === f.value ? 'pill-tab-active' : ''}`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => onToggleCustomDate(!isCustomDateOpen)}
              className={`pill-tab ${dateRange === 'custom' ? 'pill-tab-active' : ''}`}
            >
              <Calendar size={14} className="inline mr-1" />
              Custom
            </button>
          </div>
        </div>

        {/* Right: Notification bell + refresh */}
        <div className="flex items-center gap-2">
          {updatedAt && (
            <span className="hidden lg:inline text-[10px] text-gray-400">
              {updatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw size={16} className={`text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onNotificationToggle} className="relative p-2 rounded-xl hover:bg-gray-50 transition-colors">
            <Bell size={18} className="text-gray-500" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[18px] h-[18px] px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile date selector */}
      <div className="md:hidden mt-3 overflow-x-auto scrollbar-hide">
        <div className="pill-tab-container inline-flex">
          {DATE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => onDateRangeChange(f.value)}
              className={`pill-tab text-xs ${dateRange === f.value ? 'pill-tab-active' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date picker dropdown */}
      <AnimatePresence>
        {isCustomDateOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 mt-2 p-4 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 flex gap-4"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Start</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  if (date && endDate) onCustomDateChange(date, endDate);
                  else if (date) onCustomDateChange(date, date);
                }}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                inline
                calendarClassName="!border-0 !shadow-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">End</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  if (date && startDate) onCustomDateChange(startDate, date);
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                inline
                calendarClassName="!border-0 !shadow-none"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
